import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import Accordion from "react-bootstrap/Accordion";

import EmployeePayrollTable from "../tables/EmployePayrollTable";
import DDConfirmationPopup from "../popups/DDConfirmationPopup";
import TaxPaymentModal from "../popups/TaxPaymentModal";

import file_export_1 from "../../assets/images/file_export_1.svg";
import dollar_icon_1 from "../../assets/images/dollar_icon_1.svg";
import money from "../../assets/images/money.svg";
import calculator_money_icon from "../../assets/images/Calculator_Dollar_2.svg";
import taxpay from "../../assets/images/taxpayicon.svg";
import userImg from "../../assets/images/User.png";
import { exportPayrollExcel } from "../../store/slices/payrollSlice";
import { formatDate, formatCurrency } from "../../utils/dateUtils";
import { useAppSelector } from "../../hooks/useAppSelector";

function PayrollAccordion({
  companies,
  onCalculate,
  onCalculateClick,
  updatingEmployeeIds,
  onDirectDepositPayment,
  onUpdateWorkDetails,
  onGeneratePayCheck,
  openErrorPopup,
  loading,
  isCalculatingPayroll,
  onPayDDClick,
  triggerDDConfirmation,
}) {
  const dispatch = useDispatch();

  const [showPayrollModal, setShowPayrollModal] = useState(false);
  const [selectedPayrollHeaderId, setSelectedPayrollHeaderId] = useState(null);
  const [selectedCompany, setSelectedCompany] = useState(null);

  // Listen for external trigger to show DD confirmation popup
  useEffect(() => {
    if (triggerDDConfirmation) {
      setSelectedPayrollHeaderId(triggerDDConfirmation.payrollHeaderId);
      setSelectedCompany(triggerDDConfirmation.company);
      setShowPayrollModal(true);
    }
  }, [triggerDDConfirmation]);

  const [showTaxModal, setShowTaxModal] = useState(false);
  const [selectedPayrollForTax, setSelectedPayrollForTax] = useState(null);

  const [activeCycles, setActiveCycles] = useState({});

  const getActiveCycle = (company) => {
    const companyId = company.companyId;
    const savedIndex = activeCycles[companyId];

    if (company.cycles && Array.isArray(company.cycles)) {
      return company.cycles[savedIndex ?? 0];
    }
    return null;
  };

  // Handle cycle tab change
  const handleCycleChange = (companyId, index) => {
    setActiveCycles((prev) => ({
      ...prev,
      [companyId]: index,
    }));
  };

  const loadingMap = useSelector((s) => s.payroll.entityLoadingByKey);
  const generatingPaycheckId = useSelector(
    (s) => s.payroll.generatingPaycheckId,
  );
  const exportingPayrollId = useSelector((s) => s.payroll.exportingPayrollId);

  const { user } = useAppSelector((s) => s.user);
  const isAdmin = user?.role === "SuperAdmin";
  const isClient = user?.role === "Client";

  const BASE_URL = process.env.REACT_APP_IMAGE_BASE_URL || "";

  const formatMoney = (v) =>
    v !== null && v !== undefined ? `$${Number(v).toLocaleString()}` : "-";

  const handleDirectDeposit = (payrollHeaderId, company) => {
    setSelectedPayrollHeaderId(payrollHeaderId);
    setSelectedCompany(company);
    setShowPayrollModal(true);
  };

  const handleConfirmDirectDeposit = async (payrollHeaderId) => {
    try {
      await onDirectDepositPayment(payrollHeaderId, selectedCompany);
      setShowPayrollModal(false);
    } catch (e) {
      openErrorPopup(e.message || "Payment failed");
      setShowPayrollModal(false);
    }
  };

  // Helper function to transform employee data
  const transformEmployees = (employeesRaw) => {
    return employeesRaw.map((emp) => ({
      payrollEmployeeId: emp.payrollEmployeeId,
      name: emp.name,
      image: emp.image ? `${BASE_URL}${emp.image}` : userImg,
      address: emp.address,
      deductions: emp.deductions,
      taxes: emp.taxes,
      fixedSalary: emp.fixedSalary,
      incentive: emp.commissionBonus,
      totalHours: emp.totalHours,
      regularHours: emp.regularHours,
      overtime: emp.overtime,
      rate: emp.rate,
      gross: formatMoney(emp.gross),
      afterPreTax: formatMoney(emp.afterPreTax),
      totalTax: formatMoney(emp.totalTax),
      netPay: formatMoney(emp.netPay),
      netPayAfterWH: formatMoney(emp.netPayAfterWH),
      accountType: emp.accountType ?? "DD",
    }));
  };

  // Helper function to calculate totals
  const calculateTotals = (employeesRaw) => {
    const totalGross = employeesRaw.reduce((s, e) => s + (e.gross || 0), 0);
    const total = employeesRaw.reduce((s, e) => s + (e.netPayAfterWH || 0), 0);
    return { totalGross, total };
  };

  // Helper component for payroll header actions
  const PayrollHeaderActions = ({
    payroll,
    company,
    canExport,
    isExporting,
    isCalculating,
    total,
  }) => {
    const status = payroll.status;

    return (
      <div className="payroll-actions d-flex align-items-center gap_12">
        {/* Export Button */}
        <button
          type="button"
          className="btn_outline_secondary"
          disabled={!canExport || isExporting}
          onClick={(e) => {
            e.stopPropagation();
            dispatch(exportPayrollExcel(payroll.payrollHeaderId))
              .unwrap()
              .catch(() => openErrorPopup("Export failed."));
          }}
        >
          <img src={file_export_1} alt="export" />
          Export
        </button>

        {/* CALCULATE */}
        <button
          type="button"
          className="btn_outline_primary_dark"
          disabled={isCalculating || status === "Paid"}
          onClick={(e) => {
            e.stopPropagation();

            const activeCycle = getActiveCycle(company);

            if (onCalculateClick) {
              onCalculateClick(
                company.companyId,
                activeCycle?.cycleName,
                payroll.payrollFrom,
                payroll.payrollTo,
              );
            } else {
              onCalculate(
                company.companyId,
                activeCycle?.cycleName,
                payroll.payrollFrom,
                payroll.payrollTo,
              );
            }
          }}
        >
          <img src={calculator_money_icon} alt="calc" className="btn_icon" />
          Calculate DD
        </button>

        {/* PAY / PAID */}
        <button
          type="button"
          className="btn_outline_primary_light"
          disabled={total === 0 || status === "Paid"}
          onClick={(e) => {
            e.stopPropagation();
            if (onPayDDClick) {
              onPayDDClick(
                payroll.payrollHeaderId,
                company,
                payroll.payrollFrom,
                payroll.payrollTo,
              );
            } else {
              handleDirectDeposit(payroll.payrollHeaderId, company);
            }
          }}
        >
          <img src={status === "Paid" ? money : dollar_icon_1} alt="pay" />
          {status === "Paid" ? "Paid" : "Pay DD"}
        </button>
      </div>
    );
  };

  // Helper component for status badge
  const StatusBadge = ({ status }) => {
    const getBadgeClass = () => {
      switch (status) {
        case "Paid":
          return "status-badge paid";
        case "Calculated":
          return "status-badge calculated";
        case "Pending":
          return "status-badge pending";
        case "Recalculated":
          return "status-badge pending";
        default:
          return "status-badge";
      }
    };

    return <span className={getBadgeClass()}>{status || "Pending"}</span>;
  };

  // Render payroll item accordion
  const renderPayrollItem = (
    payroll,
    payrollIndex,
    company,
    companyIndex,
    isCalculating,
  ) => {
    const headerId = payroll.payrollHeaderId;
    const status = payroll.status;
    const employeesRaw = payroll.employees || [];
    const { totalGross, total } = calculateTotals(employeesRaw);
    const canExport = totalGross > 0;
    const isExporting = exportingPayrollId === headerId && canExport;
    const employees = transformEmployees(employeesRaw);
    const cycleName = payroll.cycleName;

    return (
      <Accordion.Item
        key={`${headerId}-${payroll.payrollFrom}`}
        eventKey={`${companyIndex}-${payrollIndex}`}
        className="payroll-item"
      >
        <Accordion.Header>
          <div className="payroll-header-content">
            {/* Left Section - Date & Employee Info */}
            <div className="payroll-info d-flex align-items-center gap-4 flex-wrap">
              <div className="payroll-dates">
                <span className="date-label">Payroll: </span>
                <span className="date-value">
                  {formatDate(payroll.payrollFrom)} -{" "}
                  {formatDate(payroll.payrollTo)}
                </span>
              </div>

              <div className="payroll-employees">
                <span className="employee-count">
                  <i className="fa fa-users me-1"></i>
                  {employees.length} Employee{employees.length !== 1 ? "s" : ""}
                </span>
              </div>

              {cycleName && (
                <div className="payroll-cycle">
                  <span className="cycle-badge">{cycleName}</span>
                </div>
              )}

              <div className="payroll-total">
                <span className="total-label">Total:</span>
                <span className="total-value">
                  {total ? formatCurrency(total) : formatCurrency("0")}
                </span>
              </div>

              <StatusBadge status={status} />
            </div>

            {/* Right Section - Actions */}
            <div className="payroll-actions-wrapper">
              <PayrollHeaderActions
                payroll={payroll}
                company={company}
                canExport={canExport}
                isExporting={isExporting}
                isCalculating={isCalculating}
                total={total}
              />
            </div>
          </div>
        </Accordion.Header>

        {/* Employee Table */}
        <Accordion.Body>
          <div className="employee-table-wrapper">
            <EmployeePayrollTable
              employees={employees}
              headerId={headerId}
              onUpdateWorkDetails={onUpdateWorkDetails}
              onGenerate={onGeneratePayCheck}
              loading={isCalculating}
              loadingId={generatingPaycheckId}
              updatingEmployeeIds={updatingEmployeeIds}
              canGenerate={status === "Paid"}
            />
          </div>
        </Accordion.Body>
      </Accordion.Item>
    );
  };

  // Render all payroll items for a company
  const renderPayrollItems = (company, companyIndex) => {
    const isCalculating = loadingMap[`runPayroll-${company.companyId}`];

    // If company has cycles, render based on active cycle
    if (company.cycles && company.cycles.length > 0) {
      const activeCycle = getActiveCycle(company);
      if (activeCycle?.payrollHeaders) {
        return activeCycle.payrollHeaders.map((payroll, idx) =>
          renderPayrollItem(payroll, idx, company, companyIndex, isCalculating),
        );
      }
      return null;
    }

    // Old structure without cycles
    return company.payrolls.map((payroll, idx) =>
      renderPayrollItem(payroll, idx, company, companyIndex, isCalculating),
    );
  };

  if (!Array.isArray(companies) || companies.length === 0) return null;

  return (
    <div className="accordain mt_30">
      <div className="container-fluid">
        <div className="row">
          <div className="col-md-12">
            <div className="custom_accordion">
              <Accordion defaultActiveKey="0">
                {companies.map((company, companyIndex) => {
                  return (
                    <Accordion.Item
                      key={company.companyId}
                      eventKey={String(companyIndex)}
                      className="company-accordion-item"
                    >
                      <Accordion.Header>
                        <div className="header-inline company-header">
                          {/* LEFT SIDE - Company Info */}
                          <div className="header-left-inline">
                            <div className="company_img">
                              <img
                                src={
                                  company.logo === ""
                                    ? userImg
                                    : BASE_URL + company.logo
                                }
                                alt="company"
                                onError={(e) => {
                                  e.target.src = company.logo;
                                }}
                              />
                            </div>

                            <div className="header-text">
                              <div className="header-title-row">
                                <div className="header-title">
                                  <strong>{company.companyName}</strong>
                                  <span className="pay-avg">
                                    <i className="fa fa-dollar-sign me-1"></i>
                                    Pay Avg –{" "}
                                    {formatCurrency(company.payAvg || 0)}
                                  </span>
                                </div>
                              </div>

                              <div className="header-meta">
                                <span className="meta-item">
                                  <i className="fa fa-calendar-alt me-1"></i>
                                  Previous Payroll –{" "}
                                  {formatDate(company.lastPayrollFrom)} to{" "}
                                  {formatDate(company.lastPayrollTo)}
                                </span>
                                <span className="sep">|</span>
                                <span className="meta-item">
                                  <i className="fa fa-money-bill-wave me-1"></i>
                                  Payment Date –{" "}
                                  {formatDate(company.lastPayrollPaidDate)}
                                </span>
                                <span className="sep">|</span>
                                <span className="meta-item">
                                  <i className="fa fa-repeat me-1"></i>
                                  Payroll Cycles – {company.payrolls.length}
                                </span>
                              </div>
                            </div>
                          </div>

                          {/* RIGHT SIDE - Tax Button */}
                          {company.payrolls.some(
                            (p) => p.status === "Paid",
                          ) && (
                            <button
                              className="btn_primary_light ms-auto-mr-20"
                              onClick={(e) => {
                                e.stopPropagation();
                                const paidPayroll = company.payrolls.find(
                                  (p) => p.status === "Paid",
                                );
                                setSelectedPayrollForTax({
                                  ...paidPayroll,
                                  companyId: company.companyId,
                                  taxScheduleType: company.taxScheduleType,
                                });
                                setShowTaxModal(true);
                              }}
                            >
                              <img
                                src={taxpay}
                                alt="tax"
                                className="btn_icon"
                              />
                              Pay Tax
                            </button>
                          )}
                        </div>
                      </Accordion.Header>

                      <Accordion.Body>
                        {/* Cycle Tabs - Only show if company has cycles */}
                        {company.cycles && company.cycles.length > 0 && (
                          <div className="cycle-tabs">
                            {/* <div className="cycle-tabs-header">
                              <span className="cycle-tabs-title">Payroll Cycles</span>
                            </div> */}
                            <ul className="nav nav-tabs">
                              {company.cycles.map((cycle, cycleIndex) => (
                                <li className="nav-item" key={cycleIndex}>
                                  <button
                                    className={`nav-link ${
                                      (activeCycles[company.companyId] ?? 0) ===
                                      cycleIndex
                                        ? "active"
                                        : ""
                                    }`}
                                    onClick={() =>
                                      handleCycleChange(
                                        company.companyId,
                                        cycleIndex,
                                      )
                                    }
                                  >
                                    <span className="cycle-name">
                                      {cycle.cycleName}
                                    </span>
                                    <span className="cycle-count">
                                      ({cycle.employeeCount || 0})
                                    </span>
                                  </button>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {/* Payroll Items */}
                        <div className="payroll-items">
                          <Accordion>
                            {renderPayrollItems(company, companyIndex)}
                          </Accordion>
                        </div>
                      </Accordion.Body>
                    </Accordion.Item>
                  );
                })}
              </Accordion>
            </div>
          </div>
        </div>
      </div>

      <DDConfirmationPopup
        show={showPayrollModal}
        onHide={() => setShowPayrollModal(false)}
        onConfirm={handleConfirmDirectDeposit}
        payrollHeaderId={selectedPayrollHeaderId}
        company={selectedCompany}
      />

      <TaxPaymentModal
        show={showTaxModal}
        onHide={() => setShowTaxModal(false)}
        payrollHeaderId={selectedPayrollForTax?.payrollHeaderId}
        companyId={selectedPayrollForTax?.companyId}
        payrollFrom={selectedPayrollForTax?.payrollFrom}
        payrollTo={selectedPayrollForTax?.payrollTo}
        companyTaxScheduleType={selectedPayrollForTax?.taxScheduleType}
      />
    </div>
  );
}

export default PayrollAccordion;
